/**
 * Qualifier Modal Component
 * Modal for creating and editing qualifiers
 */

import { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaTags } from 'react-icons/fa';
import { useLockBodyScroll } from '../../hooks';
import styles from './QualifierModal.module.css';

const QualifierModal = ({ isOpen, qualifier, onClose, onSuccess, createQualifier, updateQualifier }) => {
    const [formData, setFormData] = useState({
        qualifierCode: '',
        description: '',
        active: true
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const isEditMode = !!qualifier;

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    useEffect(() => {
        if (qualifier) {
            setFormData({
                qualifierCode: qualifier.qualifierCode || '',
                description: qualifier.description || '',
                active: qualifier.active !== undefined ? qualifier.active : true
            });
        } else {
            setFormData({
                qualifierCode: '',
                description: '',
                active: true
            });
        }
        setErrors({});
    }, [qualifier, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate qualifierCode
        if (!formData.qualifierCode || formData.qualifierCode.trim() === '') {
            newErrors.qualifierCode = 'Qualifier code is required';
        } else if (formData.qualifierCode.length > 50) {
            newErrors.qualifierCode = 'Qualifier code cannot exceed 50 characters';
        }

        // Validate description
        if (formData.description && formData.description.length > 254) {
            newErrors.description = 'Description cannot exceed 254 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);
            
            if (isEditMode) {
                await updateQualifier(qualifier.qualifierId, formData);
            } else {
                await createQualifier(formData);
            }

            onSuccess();
        } catch (err) {
            console.error('Error submitting form:', err);
            setErrors({
                general: err.response?.data?.message || err.message || 'Failed to save qualifier'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            onClose();
        }
    };

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            <FaTags />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>
                                {isEditMode ? 'Edit Qualifier' : 'Create New Qualifier'}
                            </h2>
                            <p className={styles.modalSubtitle}>
                                {isEditMode ? 'Update qualifier information' : 'Add a new qualifier to the system'}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Close"
                        disabled={submitting}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className={styles.modalBody}>
                    {errors.general && (
                        <div className={styles.errorAlert}>
                            {errors.general}
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label htmlFor="qualifierCode" className={styles.label}>
                            Qualifier Code <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="text"
                            id="qualifierCode"
                            name="qualifierCode"
                            value={formData.qualifierCode}
                            onChange={handleChange}
                            maxLength={50}
                            className={`${styles.input} ${errors.qualifierCode ? styles.inputError : ''}`}
                            placeholder="Enter qualifier code (e.g., Q001)"
                            disabled={submitting}
                            autoFocus
                            required
                        />
                        {errors.qualifierCode && (
                            <span className={styles.fieldError}>{errors.qualifierCode}</span>
                        )}
                        <small className={styles.helpText}>
                            {formData.qualifierCode.length}/50 characters
                        </small>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="description" className={styles.label}>
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            maxLength={254}
                            rows={4}
                            className={`${styles.textarea} ${errors.description ? styles.inputError : ''}`}
                            placeholder="Enter qualifier description (optional)"
                            disabled={submitting}
                        />
                        {errors.description && (
                            <span className={styles.fieldError}>{errors.description}</span>
                        )}
                        <small className={styles.helpText}>
                            {formData.description.length}/254 characters
                        </small>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="active"
                                checked={formData.active}
                                onChange={handleChange}
                                disabled={submitting}
                            />
                            <span>Active</span>
                        </label>
                        <small className={styles.helpText}>
                            {formData.active 
                                ? 'This qualifier is available for use' 
                                : 'This qualifier is hidden from selection'}
                        </small>
                    </div>
                </form>

                {/* Modal Footer */}
                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        onClick={handleClose}
                        className={styles.cancelButton}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        className={styles.submitButton}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className={styles.spinner}></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <FaSave /> {isEditMode ? 'Update' : 'Create'} Qualifier
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QualifierModal;
